import { useMemo } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import { getAccentColor } from "datasets/utils/color";
import { AlertCircle } from "lucide-react";
import yn from "yn";
import { Skeleton } from "~/components/ui/skeleton";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useCollapseSideNav } from "~/contexts/nextgen-sidenav";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { ROUTE_404, ROUTE_NEXTGEN_DATASETS } from "~/router/constants";
import { AccessLevelBadge } from "./AccessLevelBadge";

const BASE_NAV_ITEMS = [
  { label: "Overview", path: "overview" },
  { label: "Dashboards", path: "dashboards" },
  { label: "Tables", path: "tables" },
  { label: "Files", path: "files" },
  { label: "Agent Context", path: "agent-context" },
] as const;

const DECORATIVE_NAV_ITEMS = [{ label: "Agent Skills" }] as const;

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-accent text-accent-foreground"
      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
  );

export const DatasetDetail = () => {
  useCollapseSideNav();
  const { data: dataset, error, isLoading } = useDataset();
  const hasCohortExplorerPermission = useUserPermission("view:cohort-explorer-config-app");

  const cohortExplorerEnabled =
    yn(import.meta.env.VITE_COHORT_EXPLORER_ENABLED, { default: false }) &&
    hasCohortExplorerPermission;
  const userAccessEnabled = yn(import.meta.env.VITE_DATASET_ACCESS_ENABLED, { default: false });

  const headerBreadcrumbs = useMemo(
    () => [
      { label: "All Datasets", href: ROUTE_NEXTGEN_DATASETS },
      ...(dataset ? [{ label: dataset.title }] : []),
    ],
    [dataset]
  );

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs });

  if (error && (error as { status?: number }).status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading dataset" className="flex min-h-full">
        <div className="w-56 shrink-0 border-r border-border p-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="mx-1 my-1 h-8 rounded-md" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load dataset</p>
      </div>
    );
  }

  const accentColor = getAccentColor(dataset.accentColor);

  return (
    <div className="flex h-full overflow-hidden">
      <nav
        aria-label="Dataset sections"
        className="flex w-56 shrink-0 flex-col border-r border-border"
      >
        <div className="flex flex-col gap-3 border-b border-border px-3 py-4">
          <div
            aria-hidden="true"
            className="size-12 shrink-0 rounded-full"
            style={{ backgroundColor: accentColor }}
          />

          <div className="flex flex-col items-start gap-1.5">
            <p className="text-sm font-medium text-foreground">{dataset.title}</p>
            <AccessLevelBadge accessLevel={dataset.access_level} />
          </div>
        </div>

        <div className="flex flex-col gap-1 p-3">
          {BASE_NAV_ITEMS.map(({ label, path }) => (
            <NavLink key={path} to={path} end className={navLinkClassName}>
              {label}
            </NavLink>
          ))}
          {DECORATIVE_NAV_ITEMS.map(({ label }) => (
            <span
              key={label}
              aria-disabled="true"
              className="cursor-default rounded-md px-3 py-2 text-sm font-medium text-muted-foreground/60"
            >
              {label}
            </span>
          ))}
          {cohortExplorerEnabled && (
            <NavLink to="cohort-explorer" className={navLinkClassName}>
              Cohort Explorer
            </NavLink>
          )}
          {userAccessEnabled && (
            <NavLink to="user-access" className={navLinkClassName}>
              User Access
            </NavLink>
          )}
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-6">
        <Outlet context={dataset} />
      </div>
    </div>
  );
};
